import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, Dialog, Loading, Tag } from 'tdesign-react';
import { RefreshIcon, FileIcon } from 'tdesign-icons-react';
import {
  COMMAND_TYPE_LABEL,
  DISPATCH_STATUS_LABEL,
  DISPATCH_STATUS_THEME,
  DispatchStatus,
  SUB_CATEGORY_LABEL,
  type CommandRecord,
} from '@/services/command';
import {
  fetchMyCommandCompleteDownloadUrl,
  fetchMyCommandDownloadUrl,
  fetchMyCommandList,
} from '@/services/portal-command';
import { formatDateTime, formatDateOnly } from '@/utils/datetime';

const PAGE_SIZE = 50;

const formatSize = (bytes: number | null | undefined) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * 判断该指令是否"应展示回执"：
 *  - 必须 need_reply=1（不需回复的指令没有回执概念）；
 *  - 状态处于「已完成」或「已完成待确认」——只要处理人已提交过一次回执，就有内容可看。
 */
const hasReceipt = (item: CommandRecord) =>
  item.needReply === 1 &&
  (item.dispatchStatus === DispatchStatus.Completed ||
    item.dispatchStatus === DispatchStatus.PendingConfirm) &&
  (!!item.completeText || !!item.completeFileKey);

export interface HistoryTabHandle {
  refresh: () => void;
}

const HistoryTab = forwardRef<HistoryTabHandle>((_, ref) => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<CommandRecord[]>([]);
  const [detail, setDetail] = useState<CommandRecord | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMyCommandList({
        page: 1,
        pageSize: PAGE_SIZE,
      });
      setList(res.list);
    } catch {
      /* 错误提示由拦截器统一处理 */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useImperativeHandle(ref, () => ({ refresh: loadList }), [loadList]);

  // 下载"原指令附件"：由 fileKey 换 COS 签名 URL 后新窗口打开
  const handleDownloadCommandFile = async (fileKey: string | null) => {
    if (!fileKey) return;
    try {
      const res = await fetchMyCommandDownloadUrl(fileKey);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch {
      /* 错误提示由拦截器统一处理 */
    }
  };

  // 下载"完成回执附件"：走独立接口（后端按 complete_file_key 命中 + ownership 校验）
  const handleDownloadCompleteFile = async (completeFileKey: string | null) => {
    if (!completeFileKey) return;
    try {
      const res = await fetchMyCommandCompleteDownloadUrl(completeFileKey);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch {
      /* 错误提示由拦截器统一处理 */
    }
  };

  return (
    <div className="portal-card">
      <div className="portal-card-header">
        <div className="portal-card-title">我的指令记录</div>
        <Button variant="outline" icon={<RefreshIcon />} onClick={loadList} loading={loading}>
          刷新
        </Button>
      </div>

      <Loading loading={loading} showOverlay={false}>
        {list.length === 0 && !loading ? (
          <div className="history-empty">暂无提交记录</div>
        ) : (
          <ul className="history-list">
            {list.map((item) => (
              <li
                key={item.id}
                className="history-item history-item-clickable"
                onClick={() => setDetail(item)}
                title="点击查看详情"
              >
                <div className="history-dot-col">
                  <div className="history-dot" />
                </div>
                <div className="history-main">
                  <div className="history-tags">
                    <Tag theme="primary" variant="light">
                      {COMMAND_TYPE_LABEL[item.commandType] ?? item.commandType}
                    </Tag>
                    <Tag variant="light">
                      {SUB_CATEGORY_LABEL[item.subCategory] ?? item.subCategory}
                    </Tag>
                    {item.needReply === 1 ? (
                      <Tag theme="warning" variant="light">
                        需反馈
                      </Tag>
                    ) : null}
                    {item.needReply === 1 && item.deadlineDate ? (
                      <Tag theme="warning" variant="light">
                        截止 {formatDateOnly(item.deadlineDate)}
                      </Tag>
                    ) : null}
                  </div>
                  {item.content ? (
                    <div className="history-content" title={item.content}>
                      {item.content}
                    </div>
                  ) : null}
                  {item.fileName ? (
                    <div className="history-file">
                      <FileIcon style={{ color: '#4f7aef' }} />
                      <span
                        className="history-file-name"
                        title={item.fileName}
                        onClick={(e) => {
                          // 阻止冒泡，避免同时触发 li 的点击（弹出详情）
                          e.stopPropagation();
                          handleDownloadCommandFile(item.fileKey);
                        }}
                      >
                        {item.fileName}
                      </span>
                      <span className="history-file-size">· {formatSize(item.fileSize)}</span>
                    </div>
                  ) : null}
                  {item.completedAt ? (
                    <div className="history-completed">
                      已完成于 {formatDateTime(item.completedAt)}
                    </div>
                  ) : null}
                </div>
                <div className="history-meta">
                  <div className="history-time">{formatDateTime(item.createdAt)}</div>
                  <Tag
                    theme={DISPATCH_STATUS_THEME[item.dispatchStatus] ?? 'default'}
                    variant="light-outline"
                  >
                    {DISPATCH_STATUS_LABEL[item.dispatchStatus] ?? '未知'}
                  </Tag>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Loading>

      {/* 指令详情弹窗：只读展示；对于「需回复且已完成/已完成待确认」的指令，
          额外展示处理人回填的回执（文本 / 附件）。 */}
      <Dialog
        header="指令详情"
        visible={!!detail}
        onClose={() => setDetail(null)}
        footer={
          <Button variant="outline" onClick={() => setDetail(null)}>
            关闭
          </Button>
        }
        width={640}
        destroyOnClose
      >
        {detail ? (
          <div className="history-detail">
            <div className="history-detail-row history-detail-tags">
              <Tag theme="primary" variant="light">
                {COMMAND_TYPE_LABEL[detail.commandType] ?? detail.commandType}
              </Tag>
              <Tag variant="light">
                {SUB_CATEGORY_LABEL[detail.subCategory] ?? detail.subCategory}
              </Tag>
              <Tag
                theme={DISPATCH_STATUS_THEME[detail.dispatchStatus] ?? 'default'}
                variant="light-outline"
              >
                {DISPATCH_STATUS_LABEL[detail.dispatchStatus] ?? '未知'}
              </Tag>
              {detail.needReply === 1 ? (
                <Tag theme="warning" variant="light">
                  需反馈
                </Tag>
              ) : null}
              {detail.needReply === 1 && detail.deadlineDate ? (
                <Tag theme="warning" variant="light">
                  截止 {formatDateOnly(detail.deadlineDate)}
                </Tag>
              ) : null}
            </div>

            <div className="history-detail-row">
              <div className="history-detail-label">提交时间</div>
              <div className="history-detail-value">{formatDateTime(detail.createdAt)}</div>
            </div>

            {detail.content ? (
              <div className="history-detail-row">
                <div className="history-detail-label">指令内容</div>
                <div className="history-detail-value history-detail-text">{detail.content}</div>
              </div>
            ) : null}

            {detail.fileName ? (
              <div className="history-detail-row">
                <div className="history-detail-label">指令附件</div>
                <div className="history-detail-value history-file">
                  <FileIcon style={{ color: '#4f7aef' }} />
                  <span
                    className="history-file-name"
                    title={detail.fileName}
                    onClick={() => handleDownloadCommandFile(detail.fileKey)}
                  >
                    {detail.fileName}
                  </span>
                  <span className="history-file-size">· {formatSize(detail.fileSize)}</span>
                </div>
              </div>
            ) : null}

            {/* —— 回执块：仅需回复 + 已完成/已完成待确认 + 有实际回执内容时展示 —— */}
            {hasReceipt(detail) ? (
              <div className="history-detail-receipt">
                <div className="history-detail-receipt-title">回执内容</div>

                {detail.completedAt ? (
                  <div className="history-detail-row">
                    <div className="history-detail-label">完成时间</div>
                    <div className="history-detail-value">
                      {formatDateTime(detail.completedAt)}
                    </div>
                  </div>
                ) : null}

                {detail.completeText ? (
                  <div className="history-detail-row">
                    <div className="history-detail-label">完成说明</div>
                    <div className="history-detail-value history-detail-text">
                      {detail.completeText}
                    </div>
                  </div>
                ) : null}

                {detail.completeFileKey && detail.completeFileName ? (
                  <div className="history-detail-row">
                    <div className="history-detail-label">回执附件</div>
                    <div className="history-detail-value history-file">
                      <FileIcon style={{ color: '#4f7aef' }} />
                      <span
                        className="history-file-name"
                        title={detail.completeFileName}
                        onClick={() => handleDownloadCompleteFile(detail.completeFileKey)}
                      >
                        {detail.completeFileName}
                      </span>
                      <span className="history-file-size">
                        · {formatSize(detail.completeFileSize)}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </div>
  );
});

HistoryTab.displayName = 'HistoryTab';

export default HistoryTab;
